import * as dom from './wpstg-dom-utils.js';
import wpstg from '../wpstg.js';

/**
 * Fetch directory direct child directories
 */
export default class WpstgDirectoryNavigation {
  constructor(
      directoryListingSelector = '#wpstg-directories-listing',
      wpstgObject = wpstg,
      notyf = null,
  ) {
    this.directoryListingContainer = dom.qs(directoryListingSelector);
    this.wpstgObject = wpstgObject;
    this.dirCheckboxSelector = '.wpstg-check-dir';
    this.dirExpandSelector = '.wpstg-expand-dirs';
    this.unselectAllDirsSelector = '.wpstg-unselect-dirs';
    this.selectDefaultDirsSelector = '.wpstg-select-dirs-default';
    this.fetchChildrenAction = 'wpstg_fetch_dir_children';
    this.currentCheckboxElement = null;
    this.currentParentDiv = null;
    this.currentLoader = null;
    this.existingExcludes = [];
    this.excludedDirectories = [];
    this.isDefaultSelected = false;
    this.notyf = notyf;

    this.init();
  }

  addEvents() {
    if (this.directoryListingContainer === null) {
      console.log('Error: Unable to add directory navigation events.');
      return;
    }

    dom.addEvent(this.directoryListingContainer, 'change', this.dirCheckboxSelector, (element, event) => {
      event.preventDefault();
    });

    dom.addEvent(this.directoryListingContainer, 'click', this.dirExpandSelector, (element, event) => {
      event.preventDefault();
      if (this.toggleDirExpand(element)) {
        this.sendRequest(this.fetchChildrenAction, element);
      }
    });

    dom.addEvent(this.directoryListingContainer, 'click', this.unselectAllDirsSelector, () => {
      this.unselectAll();
    });

    dom.addEvent(this.directoryListingContainer, 'click', this.selectDefaultDirsSelector, () => {
      this.selectDefault();
    });

    dom.addEvent(this.directoryListingContainer, 'click', '.wpstg-expand-dirs', (target, event) => {
      event.preventDefault();
      this.toggleDirectoryNavigation(target);
    });

    dom.addEvent(this.directoryListingContainer, 'change', 'input.wpstg-check-dir', (target) => {
      this.updateDirectorySelection(target);
    });
  }

  init() {
    this.addEvents();
    this.parseExcludes();
  }

  /**
   * Toggle Dir Expand,
   * Return true if children aren't fetched
   * @param {HTMLElement} element
   * @return {boolean}
   */
  toggleDirExpand(element) {
    this.currentParentDiv = element.parentElement;
    const checkboxWrapper = dom.getPreviousSibling(element, 'span');
    this.currentCheckboxElement = checkboxWrapper.querySelector('input[type="checkbox"]');
    this.currentLoader = this.currentParentDiv.querySelector('.wpstg-is-dir-loading');
    if (this.currentCheckboxElement.getAttribute('data-navigatable', 'false') === 'false') {
      return false;
    }

    if (this.currentCheckboxElement.getAttribute('data-scanned', 'false') === 'false') {
      return true;
    }

    return false;
  }

  sendRequest(action) {
    if (this.currentLoader !== null) {
      this.currentLoader.style.display = 'inline-block';
    }

    const changed = this.currentCheckboxElement.getAttribute('data-changed');
    const path = this.currentCheckboxElement.getAttribute('data-path');
    const prefix = this.currentCheckboxElement.getAttribute('data-prefix');

    fetch(this.wpstgObject.ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      body: new URLSearchParams({
        action: action,
        accessToken: this.wpstgObject.accessToken,
        nonce: this.wpstgObject.nonce,
        dirPath: path,
        prefix: prefix,
        isChecked: this.currentCheckboxElement.checked,
        forceDefault: changed === 'true',
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }).then((response) => {
      if (response.ok) {
        return response.json();
      }

      return Promise.reject(response);
    }).then((data) => {
      if ('undefined' !== typeof (data.success) && data.success) {
        this.currentCheckboxElement.setAttribute('data-scanned', true);
        const dirContainer = document.createElement('div');
        dirContainer.classList.add('wpstg-dir');
        dirContainer.classList.add('wpstg-subdir');
        dirContainer.innerHTML = JSON.parse(data.directoryListing);
        this.currentParentDiv.appendChild(dirContainer);
        if (this.currentLoader !== null) {
          this.currentLoader.style.display = 'none';
        }

        dom.slideDown(dirContainer);

        return;
      }

      if (this.notyf !== null) {
        this.notyf.error(this.wpstgObject.i18n['somethingWentWrong']);
      } else {
        alert('Error: ' + this.wpstgObject.i18n['somethingWentWrong']);
      }
    }).catch((error) => {
      console.warn(this.wpstgObject.i18n['somethingWentWrong'], error);
    });
  }

  getExcludedDirectories() {
    this.excludedDirectories = [];
    this.directoryListingContainer.querySelectorAll('.wpstg-dir .wpstg--checkbox-wrapper input:not(:checked)').forEach((element) => {
      if (!this.isParentExcluded(element.value)) {
        this.excludedDirectories.push(element.value);
      }
    });

    if (!this.existingExcludes) {
      this.existingExcludes = [];
    }

    this.existingExcludes.forEach((exclude) => {
      if (!this.isParentExcluded(exclude) && !this.isScanned(exclude)) {
        this.excludedDirectories.push(exclude);
      }
    });

    return this.excludedDirectories.join(this.wpstgObject.settings?.directorySeparator);
  }

  /**
   * @param {string} path
   * @return {bool}
   */
  isParentExcluded(path) {
    let isParentAlreadyExcluded = false;
    this.excludedDirectories.forEach((dir) => {
      if (path.startsWith(dir + '/')) {
        isParentAlreadyExcluded = true;
      }
    });

    return isParentAlreadyExcluded;
  }

  getExtraDirectoriesRootOnly() {
    this.getExcludedDirectories();
    const extraDirectories = [];
    this.directoryListingContainer.querySelectorAll(':not(.wpstg-subdir)>.wpstg-dir .wpstg--checkbox-wrapper>input.wpstg-wp-non-core-dir:checked').forEach((element) => {
      extraDirectories.push(element.value);
    });

    // Check if extra directories text area exists
    // TODO: remove extraCustomDirectories code if no one require extraCustomDirectories...
    const extraDirectoriesTextArea = dom.qs('#wpstg_extraDirectories');
    if (extraDirectoriesTextArea === null || extraDirectoriesTextArea.value === '') {
      return extraDirectories.join(this.wpstgObject.settings?.directorySeparator);
    }

    const extraCustomDirectories = extraDirectoriesTextArea.value.split(/\r?\n/);

    return extraDirectories.concat(extraCustomDirectories).join(this.wpstgObject.settings.directorySeparator);
  }

  unselectAll() {
    this.directoryListingContainer.querySelectorAll('.wpstg-dir .wpstg--checkbox-wrapper input').forEach((element) => {
      element.checked = false;
    });

    this.countSelectedFiles();
  }

  selectDefault() {
    // unselect all checkboxes
    this.unselectAll();

    // only select those checkboxes whose class is wpstg-wp-core-dir
    this.directoryListingContainer.querySelectorAll('.wpstg-dir .wpstg--checkbox-wrapper input.wpstg-wp-core-dir').forEach((element) => {
      element.checked = true;
    });

    // then unselect those checkboxes whose parent has wpstg extra checkbox
    this.directoryListingContainer.querySelectorAll('.wpstg-dir > .wpstg--checkbox-wrapper .wpstg-wp-non-core-dir').forEach((element) => {
      element.parentElement.querySelectorAll('input.wpstg-wp-core-dir').forEach((element) => {
        element.checked = false;
      });
    });

    this.isDefaultSelected = true;

    this.countSelectedFiles();
  }

  parseExcludes() {
    this.existingExcludes = this.directoryListingContainer.getAttribute('data-existing-excludes', []);

    if (typeof this.existingExcludes === 'undefined' || !this.existingExcludes) {
      this.existingExcludes = [];
      return;
    }

    if (this.existingExcludes.length === 0) {
      this.existingExcludes = [];
      return;
    }

    const existingExcludes = this.existingExcludes.split(',');
    this.existingExcludes = existingExcludes.map((exclude) => {
      if (exclude.substr(0, 1) === '/') {
        return exclude.slice(1);
      }

      return exclude;
    });
  }

  isScanned(exclude) {
    let scanned = false;
    this.directoryListingContainer.querySelectorAll('.wpstg-dir> .wpstg--checkbox-wrapper>input').forEach((element) => {
      if (element.value === exclude) {
        scanned = true;
      }
    });

    return scanned;
  }

  toggleDirectoryNavigation(element) {
    const checkboxWrapper = dom.getPreviousSibling(element, 'span');
    const cbElement = checkboxWrapper.querySelector('input[type="checkbox"]');
    if (cbElement.getAttribute('data-navigatable', 'false') === 'false') {
      return;
    }

    if (cbElement.getAttribute('data-scanned', 'false') === 'false') {
      return;
    }

    const subDirectories = dom.getNextSibling(checkboxWrapper, '.wpstg-subdir');
    if (subDirectories.style.display === 'none') {
      dom.slideDown(subDirectories);
    } else {
      dom.slideUp(subDirectories);
    }
  }

  updateDirectorySelection(element) {
    const parent = element.parentElement.parentElement;
    element.setAttribute('data-changed', 'true');
    if (element.checked) {
      dom.getParents(parent.parentElement, '.wpstg-dir').forEach((parElem) => {
        for (let i = 0; i < parElem.children.length; i++) {
          if (parElem.children[i].matches('.wpstg--checkbox-wrapper')) {
            parElem.children[i].children[i].checked = true;
          }
        }
      });
      parent.parentElement.querySelectorAll('.wpstg-expand-dirs').forEach((x) => {
        if (x.textContent === 'wp-admin' || x.textContent === 'wp-includes') {
          return;
        }
        x.classList.remove('disabled');
      });
      parent.querySelectorAll('.wpstg-subdir .wpstg-check-dir').forEach((x) => {
        x.checked = true;
      });
    } else {
      parent.querySelectorAll('.wpstg-expand-dirs, .wpstg-check-subdirs').forEach((x) => {
        x.classList.add('disabled');
      });
      parent.querySelectorAll('.wpstg-dir .wpstg-check-dir').forEach((x) => {
        x.checked = false;
      });
    }

    this.countSelectedFiles();
  }

  countSelectedFiles() {
    const themesCount = this.directoryListingContainer.querySelectorAll('[data-dir-type="theme"]:checked').length;
    const pluginsCount = this.directoryListingContainer.querySelectorAll('[data-dir-type="plugin"]:checked').length;
    const filesCountElement = dom.qs('#wpstg-files-count');
    if (themesCount === 0 && pluginsCount === 0) {
      filesCountElement.classList.add('danger');
      filesCountElement.innerHTML = this.wpstgObject.i18n['noFileSelected'];
    } else {
      filesCountElement.classList.remove('danger');
      filesCountElement.innerHTML = this.wpstgObject.i18n['filesSelected'].replace('{t}', themesCount).replace('{p}', pluginsCount);
    }
  }
}
